import { supabase } from '../lib/supabase.js';

export class StrategicSummaryService {
  /**
   * Generate a comprehensive strategic summary for a student
   * Combines all available data sources into a unified narrative
   */
  static async generateStrategicSummary(userId) {
    try {
      console.log('🎯 Generating strategic summary for user:', userId);
      
      // Fetch all relevant data from the database
      const [profileResult, assessmentResult, familyResult] = await Promise.all([
        // Get academic profile
        supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', userId)
          .single(),
        
        // Get latest assessment response
        supabase
          .from('assessment_responses')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        
        // Get family member details
        supabase
          .from('family_members')
          .select('*')
          .eq('id', userId)
          .single()
      ]);
      
      const academicProfile = profileResult.data || {};
      const assessmentResponse = assessmentResult.data || {};
      const familyMember = familyResult.data || {};
      
      // Prepare data for API call - match expected format
      const profileData = {
        userId,
        academicProfile: {
          // Academic credentials
          gpa: academicProfile.gpa_weighted || academicProfile.gpa_unweighted,
          sat_score: academicProfile.sat_total,
          act_score: academicProfile.act_composite,
          ap_courses: academicProfile.ap_courses_taken,
          honors_courses: academicProfile.honors_courses,
          class_rank: academicProfile.class_rank,
          
          // Activities and achievements
          extracurriculars: academicProfile.extracurriculars,
          achievements: academicProfile.awards || academicProfile.academic_awards,
          leadership_roles: academicProfile.leadership_activities,
          awards: academicProfile.awards,
          
          // Interests and goals
          interests: academicProfile.academic_interests,
          intended_major: academicProfile.intended_major,
          career_goals: academicProfile.career_goals,
          academic_interests: academicProfile.academic_interests,
          
          // Personal context
          personal_statement: academicProfile.personal_statement,
          unique_circumstances: academicProfile.special_circumstances,
          challenges_overcome: academicProfile.challenges_overcome
        },
        
        assessmentResponses: assessmentResponse.responses || {},
        assessmentInsights: assessmentResponse.insights || {},
        
        parentObservations: {
          strengths: academicProfile.parent_observation_easy_subject,
          growth: academicProfile.parent_observation_growth,
          unique_qualities: academicProfile.parent_observation_hidden_qualities,
          surprises: academicProfile.parent_observation_surprises,
          family_context: familyMember.name || 'Student'
        },
        
        collegePreferences: academicProfile.college_preferences || [],
        familyMember
      };
      
      // Prepare request data - excluding assessmentInsights which is student-facing
      const requestData = {
        action: 'generate-strategic-summary',
        userId,
        academicProfile: profileData.academicProfile,
        assessmentResponses: profileData.assessmentResponses,
        // Don't send assessmentInsights - it's a student-facing report, not raw data
        assessmentInsights: {}, // Empty object as the API expects this field
        parentObservations: profileData.parentObservations,
        collegePreferences: profileData.collegePreferences
      };
      
      console.log('📤 Sending strategic summary request without student-facing insights');

      // Retry logic for API overload
      let lastError;
      const maxRetries = 3;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`🎯 Strategic summary generation attempt ${attempt}/${maxRetries}`);
          
          // Call the dedicated strategic summary API with timeout
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 90000); // 90 second client-side timeout
          
          const response = await fetch('/api/anthropic/analysis', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
            signal: controller.signal
          });
          
          clearTimeout(timeout);
          
          if (response.ok) {
            // Success - break out of retry loop
            const result = await response.json();
            
            if (result.success && result.strategicSummary) {
              // Use the strategic summary and executive summary from the dedicated endpoint
              const fullSummary = result.strategicSummary;
              const executiveSummary = result.executiveSummary || fullSummary.substring(0, 500) + '...';
              
              // Save the strategic summary to the database
              console.log('💾 Attempting to save strategic summary to database...');
              console.log('  - User ID:', userId);
              console.log('  - Summary length:', fullSummary.length, 'characters');
              console.log('  - Executive summary length:', executiveSummary.length, 'characters');
              
              const { data: updateData, error: updateError } = await supabase
                .from('student_academic_profiles')
                .update({
                  ai_strategic_summary: fullSummary,
                  ai_executive_summary: executiveSummary,
                  ai_strategic_summary_updated_at: new Date().toISOString()
                })
                .eq('user_id', userId)
                .select('user_id, ai_strategic_summary_updated_at');
              
              if (updateError) {
                console.error('❌ Error saving strategic summary to database:', updateError);
                console.error('  - Error code:', updateError.code);
                console.error('  - Error details:', updateError.details);
                console.error('  - Error hint:', updateError.hint);
                console.error('Please check if the columns exist in the database');
                // Don't throw - still return the summary even if save fails
              } else if (updateData && updateData.length > 0) {
                console.log('✅ Strategic summary saved successfully to database');
                console.log('  - Updated record:', updateData[0]);
                console.log('  - Timestamp:', updateData[0].ai_strategic_summary_updated_at);
              } else {
                console.warn('⚠️ Update executed but no records were updated');
                console.log('  - This might mean the user_id does not exist in student_academic_profiles');
              }
              
              console.log('📊 Summary generation complete');
              
              // Verify the save by fetching it back
              const { data: verifyData, error: verifyError } = await supabase
                .from('student_academic_profiles')
                .select('ai_strategic_summary, ai_executive_summary, ai_strategic_summary_updated_at')
                .eq('user_id', userId)
                .single();
              
              if (verifyError) {
                console.error('⚠️ Could not verify save:', verifyError);
              } else if (verifyData) {
                console.log('🔍 Verification of saved data:');
                console.log('  - Strategic summary saved:', !!verifyData.ai_strategic_summary);
                console.log('  - Strategic summary length:', verifyData.ai_strategic_summary?.length || 0);
                console.log('  - Executive summary saved:', !!verifyData.ai_executive_summary);
                console.log('  - Executive summary length:', verifyData.ai_executive_summary?.length || 0);
                console.log('  - Updated timestamp:', verifyData.ai_strategic_summary_updated_at);
              }
              
              return {
                success: true,
                strategicSummary: fullSummary,
                executiveSummary: executiveSummary,
                generatedAt: new Date().toISOString(),
                wordCount: fullSummary.split(' ').length
              };
            } else {
              throw new Error('No strategic summary returned from API');
            }
          }
          
          // Handle different error types
          const errorData = await response.json().catch(() => ({}));
          
          if (response.status === 529 || errorData.details?.includes('overloaded_error')) {
            lastError = new Error('Claude API is temporarily overloaded. Please try again in a few minutes.');
            
            if (attempt < maxRetries) {
              const delay = Math.pow(2, attempt) * 1000; // Exponential backoff: 2s, 4s, 8s
              console.log(`⏳ API overloaded, retrying in ${delay/1000} seconds...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
          } else {
            // Non-retryable error
            lastError = new Error(errorData.message || `Failed to generate strategic summary: ${response.status}`);
            break;
          }
          
        } catch (error) {
          lastError = error;
          if (attempt < maxRetries && (error.message.includes('overload') || error.name === 'AbortError')) {
            const delay = Math.pow(2, attempt) * 1000;
            console.log(`⏳ Request failed, retrying in ${delay/1000} seconds...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          break;
        }
      }
      
      // If we get here, all retries failed
      throw lastError;
      
    } catch (error) {
      console.error('Error generating strategic summary:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  /**
   * Diagnostic function to check database schema and data
   */
  static async checkDatabaseSchema(userId) {
    try {
      console.log('🔍 Checking database schema for strategic summary columns...');
      
      // Try to fetch a record to see what columns exist
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('❌ Error checking schema:', error);
        return { success: false, error: error.message };
      }
      
      if (data) {
        const columns = Object.keys(data);
        console.log('📊 Available columns in student_academic_profiles:');
        console.log('  Total columns:', columns.length);
        
        // Check for AI-related columns
        const aiColumns = columns.filter(col => col.includes('ai_') || col.includes('strategic'));
        console.log('  AI-related columns:', aiColumns);
        
        // Check specific expected columns
        const expectedColumns = ['ai_strategic_summary', 'ai_executive_summary', 'ai_strategic_summary_updated_at'];
        expectedColumns.forEach(col => {
          if (columns.includes(col)) {
            const value = data[col];
            console.log(`  ✅ ${col}: ${value ? `Has data (${typeof value === 'string' ? value.length + ' chars' : typeof value})` : 'NULL'}`);
          } else {
            console.log(`  ❌ ${col}: MISSING COLUMN`);
          }
        });
        
        return { 
          success: true, 
          columns: columns,
          aiColumns: aiColumns,
          data: {
            ai_strategic_summary: data.ai_strategic_summary ? data.ai_strategic_summary.substring(0, 100) + '...' : null,
            ai_executive_summary: data.ai_executive_summary ? data.ai_executive_summary.substring(0, 100) + '...' : null,
            ai_strategic_summary_updated_at: data.ai_strategic_summary_updated_at
          }
        };
      }
      
      return { success: false, error: 'No data found for user' };
      
    } catch (error) {
      console.error('Error in checkDatabaseSchema:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Check if a strategic summary exists and is recent
   */
  static async getStrategicSummary(userId) {
    try {
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('ai_strategic_summary, ai_executive_summary, ai_strategic_summary_updated_at')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching strategic summary:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error in getStrategicSummary:', error);
      return null;
    }
  }
  
  /**
   * Check if strategic summary needs regeneration
   * (e.g., if profile has been significantly updated since last generation)
   */
  static async needsRegeneration(userId) {
    try {
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('updated_at, ai_strategic_summary_updated_at')
        .eq('user_id', userId)
        .single();
      
      if (error || !data) {
        return true; // Needs generation if no profile exists
      }
      
      // If no strategic summary exists, needs generation
      if (!data.ai_strategic_summary_updated_at) {
        return true;
      }
      
      // Check if profile was updated after strategic summary
      const profileUpdated = new Date(data.updated_at);
      const summaryUpdated = new Date(data.ai_strategic_summary_updated_at);
      
      // Regenerate if profile was updated more than 1 hour after summary
      const oneHourInMs = 60 * 60 * 1000;
      return (profileUpdated - summaryUpdated) > oneHourInMs;
      
    } catch (error) {
      console.error('Error checking regeneration need:', error);
      return true;
    }
  }

  /**
   * Generate Version B strategic summary using enhanced prompts
   * Focuses on competitive advantage and immediate actionable tactics
   */
  static async generateStrategicSummaryB(userId) {
    try {
      console.log('🚀 Generating Version B strategic summary for user:', userId);
      
      // Fetch all relevant data from the database
      const [profileResult, assessmentResult, familyResult] = await Promise.all([
        // Get academic profile
        supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', userId)
          .single(),
        
        // Get latest assessment response
        supabase
          .from('assessment_responses')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        
        // Get family member details
        supabase
          .from('family_members')
          .select('*')
          .eq('id', userId)
          .single()
      ]);
      
      const academicProfile = profileResult.data || {};
      const assessmentResponse = assessmentResult.data || {};
      const familyMember = familyResult.data || {};
      
      // Prepare data for API call - match expected format
      const profileData = {
        userId,
        academicProfile: {
          // Academic credentials
          gpa: academicProfile.gpa_weighted || academicProfile.gpa_unweighted,
          sat_score: academicProfile.sat_total,
          act_score: academicProfile.act_composite,
          ap_courses: academicProfile.ap_courses_taken,
          honors_courses: academicProfile.honors_courses,
          class_rank: academicProfile.class_rank,
          
          // Activities and achievements
          extracurriculars: academicProfile.extracurriculars,
          achievements: academicProfile.awards || academicProfile.academic_awards,
          leadership_roles: academicProfile.leadership_activities,
          awards: academicProfile.awards,
          
          // Interests and goals
          interests: academicProfile.academic_interests,
          intended_major: academicProfile.intended_major,
          career_goals: academicProfile.career_goals,
          academic_interests: academicProfile.academic_interests,
          
          // Personal context
          personal_statement: academicProfile.personal_statement,
          unique_circumstances: academicProfile.special_circumstances,
          challenges_overcome: academicProfile.challenges_overcome
        },
        
        assessmentResponses: assessmentResponse.responses || {},
        assessmentInsights: assessmentResponse.insights || {},
        
        parentObservations: {
          strengths: academicProfile.parent_observation_easy_subject,
          growth: academicProfile.parent_observation_growth,
          unique_qualities: academicProfile.parent_observation_hidden_qualities,
          surprises: academicProfile.parent_observation_surprises,
          family_context: familyMember.name || 'Student'
        },
        
        collegePreferences: academicProfile.college_preferences || [],
        familyMember
      };
      
      // Generate conversational insights using Version B prompts
      console.log('🎯 Generating conversational insights with Version B prompts...');
      const conversationalResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'generate-conversational-insights-b',
          responses: assessmentResponse.responses || {},
          patternScores: assessmentResponse.pattern_scores || {},
          studentProfile: profileData.academicProfile,
          parentContext: profileData.parentObservations,
          conversationHistory: assessmentResponse.conversation_history || [],
          strategicContext: assessmentResponse.strategic_context || {},
          synthesisInstructions: null,
          initialQuestions: [],
          isEnhancedAssessment: true,
          assessmentId: assessmentResponse.id || userId,
          userId: userId,
          studentName: familyMember.name || 'Student'
        })
      });
      
      if (!conversationalResponse.ok) {
        const errorData = await conversationalResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to generate conversational insights: ${conversationalResponse.status}`);
      }
      
      const conversationalResult = await conversationalResponse.json();
      if (!conversationalResult.success || !conversationalResult.insights) {
        throw new Error('No conversational insights returned from API');
      }
      
      // Format insights using Version B formatting prompts
      console.log('📝 Formatting insights with Version B formatting...');
      const formatResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'format-insights-b',
          rawText: conversationalResult.insights,
          studentSpecificConsiderations: conversationalResult.studentSpecificConsiderations || '',
          personalityProfile: conversationalResult.personalityProfile || '',
          studentName: familyMember.name || 'Student',
          synthesisGuidance: 'Focus on strategic advantage and immediate tactical actions',
          formattingInstructions: 'Use action-oriented language with clear next steps',
          sectionTitles: ['Strategic Advantage', 'Tactical Actions', 'Competitive Intelligence'],
          tonePreferences: 'strategic',
          metaphorStyle: 'tactical'
        })
      });
      
      if (!formatResponse.ok) {
        const errorData = await formatResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to format insights: ${formatResponse.status}`);
      }
      
      const formatResult = await formatResponse.json();
      if (!formatResult.success || !formatResult.formattedSummary) {
        throw new Error('No formatted summary returned from API');
      }
      
      const fullSummary = formatResult.formattedSummary;
      
      // Extract executive summary focused on strategic advantage
      const lines = fullSummary.split('\n');
      const strategicAdvantageSection = lines.find(line => 
        line.toLowerCase().includes('strategic advantage') || 
        line.toLowerCase().includes('competitive edge')
      );
      const executiveSummary = strategicAdvantageSection 
        ? strategicAdvantageSection.substring(0, 500) + '...'
        : fullSummary.substring(0, 500) + '...';
      
      // Save the strategic summary to the database
      const { error: updateError } = await supabase
        .from('student_academic_profiles')
        .update({
          ai_strategic_summary: fullSummary,
          ai_executive_summary: executiveSummary,
          ai_strategic_summary_updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);
      
      if (updateError) {
        console.error('Error saving Version B strategic summary to database:', updateError);
        // Don't throw - still return the summary even if save fails
      }
      
      console.log('✅ Version B strategic summary generated and saved successfully');
      console.log(`📊 Summary length: ${fullSummary.length} characters`);
      
      return {
        success: true,
        strategicSummary: fullSummary,
        executiveSummary: executiveSummary,
        generatedAt: new Date().toISOString(),
        wordCount: fullSummary.split(' ').length,
        version: 'B'
      };
      
    } catch (error) {
      console.error('Error generating Version B strategic summary:', error);
      return {
        success: false,
        error: error.message,
        version: 'B'
      };
    }
  }

  /**
   * Helper method to get student profile data for Version B components
   */
  static async getStudentProfileData(userId) {
    try {
      const [profileResult, assessmentResult, familyResult] = await Promise.all([
        supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', userId)
          .single(),
        
        supabase
          .from('assessment_responses')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        
        supabase
          .from('family_members')
          .select('*')
          .eq('id', userId)
          .single()
      ]);

      const academicProfile = profileResult.data || {};
      const assessmentResponse = assessmentResult.data || {};
      const familyMember = familyResult.data || {};

      return {
        basicInfo: {
          name: familyMember.name,
          email: familyMember.email,
          grade: familyMember.grade
        },
        academicProfile: {
          gpa: academicProfile.gpa_weighted || academicProfile.gpa_unweighted,
          intended_major: academicProfile.intended_major,
          extracurriculars: academicProfile.extracurriculars,
          achievements: academicProfile.awards || academicProfile.academic_awards,
          test_scores: academicProfile.sat_total || academicProfile.act_composite
        },
        assessmentResponses: assessmentResponse.responses,
        assessmentInsights: assessmentResponse.insights,
        familyMember
      };
    } catch (error) {
      console.error('Error fetching student profile data:', error);
      return null;
    }
  }

  /**
   * Generate a 150-word executive summary from the strategic summary
   * Creates a memorable elevator pitch for the student
   */
  static async generateExecutiveSummary(strategicSummary, userId) {
    try {
      console.log('📝 Generating executive summary for user:', userId);
      
      // Get student info for personalization
      const { data: studentData } = await supabase
        .from('family_members')
        .select('name, grade')
        .eq('id', userId)
        .single();
      
      const studentAge = studentData?.grade ? 18 - (12 - parseInt(studentData.grade)) : 17;
      
      const response = await fetch('/api/anthropic/strategic-features', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'generate-executive-summary',
          strategicSummary,
          studentAge,
          studentName: studentData?.name,
          targetAudience: 'student'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate executive summary');
      }

      const result = await response.json();
      
      // Save to database
      const { error: updateError } = await supabase
        .from('student_academic_profiles')
        .update({
          ai_executive_summary: result.executiveSummary,
          ai_executive_summary_updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error saving executive summary:', updateError);
      }

      return result.executiveSummary;
    } catch (error) {
      console.error('Error generating executive summary:', error);
      throw error;
    }
  }

  /**
   * Parse and format assessment insights into empowering discoveries
   * Transforms raw assessment text into actionable insights
   */
  static async parseAssessmentInsights(rawInsights, userId) {
    try {
      console.log('🧠 Parsing assessment insights for user:', userId);
      
      // Get student name for personalization
      const { data: studentData } = await supabase
        .from('family_members')
        .select('name')
        .eq('id', userId)
        .single();
      
      const response = await fetch('/api/anthropic/strategic-features', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'parse-assessment-insights',
          rawInsights,
          studentName: studentData?.name,
          formatStyle: 'empowering'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to parse assessment insights');
      }

      const result = await response.json();
      
      // Update the assessment response with formatted insights
      const { error: updateError } = await supabase
        .from('assessment_responses')
        .update({
          formatted_insights: result.insights,
          insights_updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (updateError) {
        console.error('Error saving formatted insights:', updateError);
      }

      return result.insights;
    } catch (error) {
      console.error('Error parsing assessment insights:', error);
      throw error;
    }
  }
}

// Export as default for compatibility
export default StrategicSummaryService;